import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  StatusTypeToDisplayNameMapping,
  Task,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {PMEIProcedureQuery} from 'procedures/worksheets/__generated/PMEIProcedureQuery';
import {
  PMEIsQuery_procedureMethodExecutionInstances as PMEIOrInaccessible,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask as PMEIMethodTask,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode_statusChanges_ProcessTaskStatusChangeNode as StatusChangeNode,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_taskTransitionExtensions_TaskTransitionExtensionNode as TaskTransitionExtensionNode,
} from 'procedures/worksheets/__generated/PMEIsQuery';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import {HasAvatar} from 'ui-library/avatar/Avatar';

interface StatusChangeInfo {
  creator?: HasAvatar;
  timestamp: string;
}

function extractStatusChangeInfo(
  statusType: WorksheetTaskStatusType,
  statusChanges: Array<StatusChangeNode>,
  taskTransitionExtensions: Array<TaskTransitionExtensionNode>
): StatusChangeInfo | null {
  const creator = statusChanges.find(
    (statusChange) => statusChange.newStatus.displayName === StatusTypeToDisplayNameMapping[statusType]
  )?.creator;

  const timestamp = taskTransitionExtensions.find(
    (ext) => ext.statusDisplayName === StatusTypeToDisplayNameMapping[statusType]
  )?.timestampUtcIsoformat;

  return timestamp ? {creator, timestamp} : null;
}

export function getProcedureType(pmeis?: Array<PMEIOrInaccessible>): ProcedureType | undefined {
  if (!pmeis || !pmeis[0] || isInaccessibleInfo(pmeis[0])) {
    return undefined;
  }
  const allEqual = pmeis.every(
    (pmei) =>
      !isInaccessibleInfo(pmei) &&
      !isInaccessibleInfo(pmeis[0]) &&
      pmei.procedureRun.procedureType === pmeis[0].procedureRun.procedureType
  );
  assert(allEqual, 'All PMEIs should have the same procedure type');
  return pmeis[0].procedureRun.procedureType;
}

export function extractProcedureRunDataFromTask(
  task?: PMEIMethodTask,
  pmei?: PMEIOrInaccessible
): {
  unitOperationName?: string;
  replicateNumber?: number;
  conditionName?: string;
  runStarted?: StatusChangeInfo | null;
  runEnded?: StatusChangeInfo | null;
  procedureType?: ProcedureType;
} {
  if (!task || isInaccessibleInfo(task) || !pmei || isInaccessibleInfo(pmei)) {
    return {};
  }

  const {statusChanges} = task;
  const {planMethodDefinitionVersion, procedureRunConditionReplicate, procedureRun} = pmei;

  const runStarted = extractStatusChangeInfo(
    WorksheetTaskStatusType.IN_PROGRESS,
    statusChanges,
    pmei.taskTransitionExtensions
  );
  const runEnded = extractStatusChangeInfo(
    WorksheetTaskStatusType.COMPLETED,
    statusChanges,
    pmei.taskTransitionExtensions
  );

  return {
    unitOperationName: planMethodDefinitionVersion.name,
    replicateNumber: procedureRunConditionReplicate.replicateNumber,
    conditionName: procedureRunConditionReplicate.procedureRunCondition.name,
    runStarted,
    runEnded,
    procedureType: procedureRun.procedureType,
  };
}

export function areAllMethodTasksCreated(pmeis: Array<PMEIOrInaccessible>, tasks: Array<Task>): boolean {
  const taskIds = tasks
    .map((task) => (task.__typename === 'ProcessTaskNode' ? task.id : ''))
    .filter((id) => id);
  return (
    (pmeis.length === 0 && tasks.length !== 0) ||
    (pmeis.length === tasks.length &&
      tasks.length !== 0 &&
      pmeis.every(
        (pmei) =>
          pmei.__typename === 'ProcedureMethodExecutionInstanceNode' &&
          pmei.methodTask &&
          pmei.methodTask.__typename === 'ProcessTaskNode' &&
          taskIds.includes(pmei.methodTask.id)
      ))
  );
}

type MethodExecutionInstance = ExtractByTypename<
  PMEIProcedureQuery['procedureMethodExecutionInstance'],
  'ProcedureMethodExecutionInstanceNode'
>['procedureRun']['methodExecutionInstances'][number];

export function getConditionReplicateText(pmei: MethodExecutionInstance): string {
  return `${pmei.procedureRunConditionReplicate.procedureRunCondition.name} - ${pmei.procedureRunConditionReplicate.replicateNumber}`;
}

/**
 * Generates a string describing the conditions being executed in a worksheet.
 *
 * - If there is exactly one condition-replicate being executed in the worksheet, returns a label with the condition name and replicate number.
 * - If all method execution instances are being executed the worksheet, returns 'All conditions'
 * - Otherwise, returns a label indicating the number of conditions being executed in the worksheet.
 */
export function getConditionsLabel(
  worksheetId: string,
  procedureType: ProcedureType,
  methodExecutionInstances: Array<MethodExecutionInstance>
): string {
  assert(
    methodExecutionInstances.length > 0,
    'Expected all procedure runs to have at least one method execution instance'
  );
  const pmeisForWorksheet = methodExecutionInstances.filter(
    (pmei) => !isInaccessible(pmei.worksheet) && pmei.worksheet.id === worksheetId
  );

  if (pmeisForWorksheet.length === 1) {
    return getConditionReplicateText(pmeisForWorksheet[0]);
  }

  assert(procedureType === ProcedureType.RECIPE, 'Only recipes support parallel execution mode');

  const uniqueConditions = new Set(
    pmeisForWorksheet.map((pmei) => pmei.procedureRunConditionReplicate.procedureRunCondition.id)
  );
  return `${uniqueConditions.size} ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: uniqueConditions.size > 1})}`;
}
